const CART_UPDATE_URL = '/cart/update.js';
const SYNC_EVENT = 'charity-cart:changed';
const PILL_SELECTOR = '.charity-cart__pill';

class CharityCartComponent extends HTMLElement {
  #activeFetch = null;

  connectedCallback() {
    this.refs = {
      name: this.querySelector('[data-ref="name"]'),
      desc: this.querySelector('[data-ref="desc"]'),
      label: this.querySelector('[data-ref="label"]'),
      pct: this.querySelector('[data-ref="pct"]'),
      fill: this.querySelector('[data-ref="fill"]'),
      progress: this.querySelector('[data-ref="progress"]'),
      liveRegion: this.querySelector('[data-ref="liveRegion"]'),
    };

    // Delegated handlers on the host element survive any subtree replacement
    // (e.g. if data-skip-subtree-update is ever removed from the snippet, or
    // if the component is rendered in a context that doesn't use the morph).
    this.addEventListener('click', this.onDelegatedClick);
    this.addEventListener('keydown', this.onDelegatedKeydown);

    document.addEventListener(SYNC_EVENT, this.onSyncEvent);

    this.maybeWriteDefaultOnConnect();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onDelegatedClick);
    this.removeEventListener('keydown', this.onDelegatedKeydown);
    document.removeEventListener(SYNC_EVENT, this.onSyncEvent);
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  // Always re-query so a stale cached array can never point at detached nodes.
  get pills() {
    return Array.from(this.querySelectorAll(PILL_SELECTOR));
  }

  maybeWriteDefaultOnConnect() {
    const currentId = this.dataset.currentId || '';
    const stale = this.dataset.currentStale === 'true';
    const resolvedHandle = this.dataset.resolvedHandle || '';

    // Stale pointer: the cart still carries a _charity_id for a charity
    // the merchant has since deleted. Snippet has already rendered the
    // fallback active slot — rewrite the cart attributes to match it.
    if (stale) {
      const activePill = this.pills.find((p) => p.dataset.handle === resolvedHandle);
      if (activePill) {
        this.dataset.currentId = resolvedHandle;
        void this.writeAttributes(activePill);
      } else {
        console.warn('[charity-cart] stale _charity_id but no pill matched resolved handle; skipping rewrite');
      }
      return;
    }

    if (currentId) return;

    const defaultSlot = this.dataset.defaultSlot || 'none';
    if (defaultSlot === 'none' || !defaultSlot) return;

    const pill = this.pills.find((p) => p.dataset.slot === defaultSlot);
    if (!pill) return;

    this.applySelection(pill, { silent: true, broadcast: false });
    void this.writeAttributes(pill);
  }

  onDelegatedClick = (event) => {
    const pill = event.target instanceof Element ? event.target.closest(PILL_SELECTOR) : null;
    if (!pill || !this.contains(pill)) return;
    if (pill.classList.contains('is-active')) return;
    this.applySelection(pill, { silent: false, broadcast: true });
    void this.writeAttributes(pill);
  };

  onDelegatedKeydown = (event) => {
    const pill = event.target instanceof Element ? event.target.closest(PILL_SELECTOR) : null;
    if (!pill || !this.contains(pill)) return;
    const { key } = event;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;
    event.preventDefault();
    const pills = this.pills;
    const idx = pills.indexOf(pill);
    if (idx === -1 || pills.length === 0) return;
    let nextIdx;
    if (key === 'ArrowLeft') nextIdx = (idx - 1 + pills.length) % pills.length;
    else if (key === 'ArrowRight') nextIdx = (idx + 1) % pills.length;
    else if (key === 'Home') nextIdx = 0;
    else nextIdx = pills.length - 1;
    const next = pills[nextIdx];
    next.focus();
    this.applySelection(next, { silent: false, broadcast: true });
    void this.writeAttributes(next);
  };

  onSyncEvent = (event) => {
    const { handle, source } = event.detail || {};
    if (!handle || source === this) return;
    const pill = this.pills.find((p) => p.dataset.handle === handle);
    if (!pill || pill.classList.contains('is-active')) return;
    this.applySelection(pill, { silent: true, broadcast: false });
  };

  applySelection(pill, { silent, broadcast }) {
    const pills = this.pills;
    pills.forEach((p) => {
      const isThis = p === pill;
      p.classList.toggle('is-active', isThis);
      p.setAttribute('aria-checked', isThis ? 'true' : 'false');
      p.setAttribute('tabindex', isThis ? '0' : '-1');
    });

    const { name, description, label, percent, color, handle } = pill.dataset;

    if (this.refs.name) this.refs.name.textContent = name;
    if (this.refs.desc) this.refs.desc.textContent = description;
    if (this.refs.label) this.refs.label.textContent = label;
    if (this.refs.pct) this.refs.pct.textContent = `${percent}% funded`;
    if (this.refs.fill) this.refs.fill.style.width = `${percent}%`;
    if (this.refs.progress) this.refs.progress.setAttribute('aria-valuenow', percent);

    const card = this.querySelector('.charity-cart__card');
    if (card) card.style.setProperty('--charity-accent', color);

    this.dataset.currentId = handle;

    if (!silent && this.refs.liveRegion) {
      this.refs.liveRegion.textContent = `${name} selected, ${percent}% funded`;
    }

    if (broadcast) {
      document.dispatchEvent(new CustomEvent(SYNC_EVENT, {
        detail: { handle, source: this },
      }));
    }
  }

  async writeAttributes(pill) {
    if (this.#activeFetch) this.#activeFetch.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    const { name, handle } = pill.dataset;
    const donationPercent = this.dataset.donationPercent || '';
    const body = {
      attributes: {
        Charity: name,
        _charity_id: handle,
        _charity_donation_percent: donationPercent,
      },
    };

    try {
      const response = await fetch(CART_UPDATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        console.error('[charity-cart] cart update returned', response.status);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[charity-cart] failed to update cart attributes', err);
      }
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('charity-cart-component')) {
  customElements.define('charity-cart-component', CharityCartComponent);
}
